import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/dialog';
import { useCan } from '@/HireWithUs/Context/CompanyAccessContext';
import { getJobPeople, setJobPeople } from '../Settings/teamApi';

// Each place on a job takes people whose role has one scope, in this order. The
// server says which place each person can take; an older server only sends the
// role, and then the built-in role of the same name decides.
const placeOf = (person) => person.place || person.role?.key;

const PLACES = [
  { relation: 'hr_manager', label: 'HR Managers', hint: 'Oversee this job for their team.' },
  { relation: 'recruiter', label: 'Recruiters', hint: 'Work the job day to day.' },
  { relation: 'hiring_manager', label: 'Hiring managers', hint: 'Review candidates and decide.' },
];

const key = (userId, relation) => `${relation}:${userId}`;

/**
 * Who works on a job. People in a role limited to some jobs see a job only once
 * they're on it here, or through a team. Saving replaces the whole list; whoever
 * posted the job keeps it regardless.
 */
export default function JobPeopleDialog({ jobId, jobTitle, open, onOpenChange }) {
  const canManageTeam = useCan('team.manage');
  const [choices, setChoices] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !jobId) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    getJobPeople(jobId)
      .then(({ people, choices: options }) => {
        if (!active) return;
        setChoices(options);
        setSelected(new Set(people.filter((p) => p.relation !== 'creator').map((p) => key(p.id, p.relation))));
      })
      .catch((err) => {
        if (active) setError(err?.response?.data?.error || 'Could not load the people on this job.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [open, jobId]);

  const byPlace = useMemo(
    () => PLACES.map((place) => ({ ...place, people: choices.filter((p) => placeOf(p) === place.relation) })),
    [choices]
  );

  const toggle = (userId, relation) => {
    setSelected((current) => {
      const next = new Set(current);
      const id = key(userId, relation);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const people = [...selected].map((id) => {
        const [relation, userId] = id.split(':');
        return { user_id: Number(userId), relation };
      });
      await setJobPeople(jobId, people);
      toast.success('People on this job updated');
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const nobodyToAssign = !loading && choices.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>People on this job</DialogTitle>
          <DialogDescription>
            {jobTitle ? `${jobTitle}. ` : ''}People whose role covers only some jobs see this one once they're on it.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading people" />
          </div>
        ) : nobodyToAssign ? (
          <p className="py-4 text-sm text-muted-foreground">
            Nobody on your team is an HR Manager, Recruiter or Hiring Manager yet.
            {canManageTeam ? (
              <> Invite them from <Link to="/hirewithus/settings/team" className="font-medium text-primary underline">Team &amp; Roles</Link>.</>
            ) : null}
          </p>
        ) : (
          <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto pr-1">
            {byPlace.filter((place) => place.people.length > 0).map((place) => (
              <fieldset key={place.relation} className="flex flex-col gap-1.5">
                <legend className="text-sm font-semibold text-foreground">{place.label}</legend>
                <p className="text-xs text-muted-foreground">{place.hint}</p>
                {place.people.map((person) => (
                  <label key={person.id} className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-sm hover:bg-muted/50">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={selected.has(key(person.id, place.relation))}
                      onChange={() => toggle(person.id, place.relation)}
                      disabled={saving}
                    />
                    <span className="min-w-0 flex-1 truncate">{person.name}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {person.role?.name && person.role.key !== place.relation ? `${person.role.name} · ` : ''}
                      {person.status === 'invited' ? 'Invited' : person.email}
                    </span>
                  </label>
                ))}
              </fieldset>
            ))}
          </div>
        )}

        {error ? (
          <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button type="button" onClick={save} disabled={saving || loading || nobodyToAssign}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
